// ErrorBoundary.js
import React from "react";
import { AlertCircle, RefreshCw, Home } from "lucide-react";
import "./ErrorBoundary.css";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
    this.resetErrorBoundary = this.resetErrorBoundary.bind(this);
  }

  static getDerivedStateFromError(error) {
    // Generate a unique error ID for tracking
    const errorId = `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    this.setState({
      errorInfo,
    });

    // In production, you could send this to an error tracking service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  resetErrorBoundary() {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  }

  render() {
    if (this.state.hasError) {
      const { error, errorId } = this.state;
      const { fallback, onReset, showDetails = false } = this.props;

      // Custom fallback UI if provided
      if (fallback) {
        return fallback({ error, errorId, resetError: this.resetErrorBoundary });
      }

      // Default fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <AlertCircle size={48} />
            </div>
            <h2 className="error-boundary-title">Something went wrong</h2>
            <p className="error-boundary-message">
              {error?.message || "An unexpected error occurred"}
            </p>
            {errorId && (
              <p className="error-boundary-id">Error ID: {errorId}</p>
            )}

            {showDetails && this.state.errorInfo && (
              <details className="error-boundary-details">
                <summary>Error Details</summary>
                <pre className="error-boundary-stack">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}

            <div className="error-boundary-actions">
              <button
                className="error-boundary-button error-boundary-button-primary"
                onClick={() => {
                  this.resetErrorBoundary();
                  if (onReset) {
                    onReset();
                  }
                }}
              >
                <RefreshCw size={16} />
                Try Again
              </button>
              <button
                className="error-boundary-button error-boundary-button-secondary"
                onClick={() => {
                  window.location.href = "/";
                }}
              >
                <Home size={16} />
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
